import React, { useRef, useState } from "react";
import Modal from "react-modal";
import { BsFiletypeCsv, BsXLg } from "react-icons/bs";
import Papa from "papaparse";
import { toast } from "react-toastify";

import { validate, hasValidationErrors, getInitialErrors } from "../validation/uploadBulkValidationRules";
import Button from "./button";


const BulkUploadPopup = (props) => {
    const {
        isPopupVisible,
        handleClosePopup,
        title,
        columns = [],
        validationRules = [],
        transformedRowValues,
        getTableRowValues,
        onSubmit,
        label,
        isSubmit,
    } = props;

    const fileInputRef = useRef(null);

    const [values, setValues] = useState([]);
    const [fieldErrors, setFieldErrors] = useState(() => getInitialErrors(validationRules));

    const handleClose = () => {
        setValues([]);
        setFieldErrors(getInitialErrors(validationRules));
        handleClosePopup();
    };

    const handleUploadCSV = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase(),
            complete: (data) => {
                const rows = data.data;

                if (!rows || rows.length === 0) {
                    return;
                }

                const newData = rows.map((row) => transformedRowValues(row));

                setValues((prevValues) => [...prevValues, ...newData]);
                setFieldErrors(getInitialErrors(validationRules));
                fileInputRef.current.value = "";
            },
        });
    };

    const handleReUpload = () => {
        setValues([]);
        setFieldErrors(getInitialErrors(validationRules));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleSubmit = () => {
        if (values.length === 0) {
            toast.error("Please upload a CSV file first");
            return;
        }

        const errors = validate(values, validationRules);
        setFieldErrors(errors);

        if (hasValidationErrors(errors)) {
            toast.error("Validation failed. Please upload the right format CSV file with the required fields");
            return;
        }

        onSubmit(values);
    };


    return (
        <Modal
            isOpen={isPopupVisible}
            onRequestClose={handleClose}
            className="modal_body_lg"
            overlayClassName="modal_overlay"
        >
            <div className="box_wrapper">
                <div className="d-flex justify-content-between gap-2 align-items-center fw-bold mb-3 px-2">
                    <h4 className="mb-0">{title}</h4>
                    <BsXLg size={20} onClick={handleClose} style={{ cursor: 'pointer' }} />
                </div>
                <div className="modal_content_lg table-responsive mt-1">
                    <table className="table">
                        <thead className="table-light table-bordered position-relative top-0 left-0 w-100">
                            <tr>
                                <th>S.no</th>
                                {columns.map((column) => (
                                    <th key={column.key}>
                                        {column.label}
                                        {fieldErrors[column.key]?.length > 0 && (
                                            <p className="error_message">{fieldErrors[column.key].join(", ")}</p>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {values?.length > 0 ? (
                                values.map((value, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        {getTableRowValues(value, index)}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length + 1} style={{ height: '400px', verticalAlign: 'middle' }}>
                                        <div className="d-flex flex-column justify-content-center align-items-center gap-2 fw-bold fs-5 text-muted h-100">
                                            Please upload a CSV file.
                                            <span className="fw-normal fs-6">
                                                Several values in one cell? Separate them with <b>;</b> — e.g. <code>Steven D. Levitt; Stephen J. Dubner</code>
                                            </span>
                                            <div className="d-flex gap-2 align-items-center mt-3">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={handleUploadCSV}
                                                    style={{ display: "none" }}
                                                />
                                                <BsFiletypeCsv
                                                    size={50}
                                                    className="text-dark cursor-pointer border border-dark rounded-2"
                                                    style={{
                                                        padding: '8px',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => fileInputRef.current?.click()}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <p className="mb-0 text-muted">
                        {values.length > 0
                            ? <span className="fw-bold text-dark">{values.length} {label}{values.length > 1 ? 's' : ''} ready to upload</span>
                            : <span>No {label}s uploaded yet</span>
                        }
                    </p>
                    <div className="d-flex gap-2">
                        {values.length > 0 && <button type="button" className="btn btn-outline-dark" onClick={handleReUpload}>Re-Upload <BsFiletypeCsv size={16} /></button>}
                        <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Submitting ..." text="Submit" />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default BulkUploadPopup;

